/**
 * Credit balance and credit purchase API client.
 */

import { BaseApiClient } from './base'
import type { CreditPack, CreditPurchase } from '../../types'

export interface CreditBalance {
  balance: number
  currency: string
}

export interface CreditPacksResponse {
  packs: CreditPack[]
}

export interface CreditPurchaseResponse {
  success: boolean
  transaction_id?: string
  purchase_id?: string
  credit_amount?: number
  new_balance?: number
  message: string
  // Stripe payment fields (when payment requires frontend confirmation)
  client_secret?: string
  requires_payment?: boolean
  checkout_url?: string
}

export interface CreditPurchasesResponse {
  purchases: CreditPurchase[]
}

export interface PurchaseStatusResponse {
  purchase_id: string
  status: 'pending' | 'completed' | 'failed'
  credit_amount?: number
  new_balance?: number
}

export class CreditsApi extends BaseApiClient {
  /**
   * Get user's current credit balance
   */
  async getBalance(): Promise<CreditBalance> {
    return this.request('/credits/balance')
  }

  /**
   * Get all available credit packs
   */
  async getPacks(): Promise<CreditPacksResponse> {
    return this.request('/credits/packs')
  }

  /**
   * Purchase a credit pack
   */
  async purchaseCredits(packId: string): Promise<CreditPurchaseResponse> {
    return this.request('/credits/purchase', {
      method: 'POST',
      body: JSON.stringify({ pack_id: packId }),
    })
  }

  /**
   * Get user's credit purchase history
   */
  async getPurchaseHistory(limit: number = 50): Promise<CreditPurchasesResponse> {
    return this.request(`/credits/purchases?limit=${limit}`)
  }

  /**
   * Check the status of a specific purchase (for polling after Stripe checkout)
   */
  async getPurchaseStatus(purchaseId: string): Promise<PurchaseStatusResponse> {
    return this.request(`/credits/purchase/${purchaseId}/status`)
  }
}

// Export singleton instance for static access (backward compatibility)
export const CreditsAPI = new CreditsApi()

